import React, { FormEvent } from "react";
import { Helmet } from "react-helmet";
import { Controller } from "react-hook-form";
import { Box } from "../../../shared/components/ui/chakra";
import Button from "../../../shared/components/ui/Button";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Radio from "../../../shared/components/ui/Radio";
import SectionTitle from "../../../shared/components/app/SectionTitle";
import SiteSeal from "../../../shared/components/app/SiteSeal";
import { Options } from "../../../shared/lib/types";
import useAutokana from "../../../shared/lib/useAutokana";
import { useStep1Form } from "../lib/useStep1Form";
import fillAddressByPostalCode from "../../../../shared/lib/fillAddressByPostalCode";
import {
  NameField,
  NameKanaField,
  PostalCodeField,
  PrefectureField,
  Address1Field,
  Address2Field,
  Address3Field,
  PhoneNumberField,
  EmailField,
  IndustryField,
  EmployeesField,
  EstablishmentYearField,
  EstablishmentMonthField,
  CapitalField,
  PresidentNameField,
  WebsiteField,
  FacebookUrlField,
  InstagramUrlField,
  NoteUrlField,
  PinterestUrlField,
  ThreadsUrlField,
  TiktokUrlField,
  XUrlField,
  YoutubeUrlField,
  BranchNameField,
  ShowUpToAddress1Field,
} from "./FormFields";
import { CroppableImageFileDropzone } from "../../../../shared/components/CroppableImageFileDropzone";
import useFlash from "../../../shared/lib/useFlash";
import { validateFileSize } from "../../../../shared/lib/validateFileSize";
import { createUnifiedFile } from "../../../../shared/lib/UnifiedFile";
import { businessTypes } from "../../../../shared/lib/businessTypes";


import {
  Divider,
  Flex,
  Heading,
  ListItem,
  RadioGroup,
  Stack,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";

const Step1 = ({
  methods,
  prefectures,
  industries,
  onSubmit,
}: {
  methods: ReturnType<typeof useStep1Form>;
  prefectures: Options;
  industries: Options;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) => {
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = methods;
  const nameAutoKana = useAutokana("#name", "#name_kana");
  const showFlash = useFlash();

  return (
    <Box>
      <Helmet>
        <title>
          事業者情報 登録｜建築士のためのコミュニティA-Loop【エーループ】
        </title>
      </Helmet>
      <Heading size="lg" textAlign="center">
        事業者情報 登録
      </Heading>
      <Box as="form" onSubmit={onSubmit} mt={10}>
        <SectionTitle>事業者情報</SectionTitle>
        <Box mt={8}>
          支店でご利用の場合は、支店の住所、電話番号などを登録してください。
        </Box>
        <Stack gap={7} mt={7}>
          <Stack gap={2}>
            <FormLabel required>事業者区分</FormLabel>
            <Controller
              name="business_type"
              control={control}
              render={({ field, fieldState }) => {
                const { ref, ...rest } = field;
                return (
                  <>
                    <RadioGroup {...rest}>
                      <Stack>
                        {businessTypes.map((businessType) => (
                          <Radio
                            value={businessType.value}
                            key={businessType.value}
                            ref={ref}
                          >
                            {businessType.label}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                    {fieldState.error?.message !== undefined && (
                      <InputError>{fieldState.error.message}</InputError>
                    )}
                  </>
                );
              }}
            />
          </Stack>
          <Box w="full">
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <NameField
                  field={field}
                  fieldState={fieldState}
                  onInput={() => {
                    setValue("name_kana", nameAutoKana?.getFurigana() ?? "");
                  }}
                />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="name_kana"
              control={control}
              render={({ field, fieldState }) => (
                <NameKanaField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="branch_name"
              control={control}
              render={({ field, fieldState }) => (
                <BranchNameField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box>
            <FormLabel required>事業者所在地</FormLabel>
            <Stack
              mt={{ base: 2, md: 3 }}
              pl={5}
              gap={6}
              borderLeft="1px solid"
              borderColor="primary"
            >
              <Box maxW="326px">
                <Controller
                  name="postal_code"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PostalCodeField
                      field={field}
                      fieldState={fieldState}
                      onChange={async (e) => {
                        field.onChange(e);
                        await fillAddressByPostalCode({
                          prefectures,
                          postalCode: e.target.value,
                          form: methods,
                        });
                      }}
                    />
                  )}
                />
              </Box>
              <Box maxW="326px">
                <Controller
                  name="prefecture_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <PrefectureField
                      field={field}
                      fieldState={fieldState}
                      prefectures={prefectures}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="address1"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Address1Field field={field} fieldState={fieldState} />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="address2"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Address2Field field={field} fieldState={fieldState} />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="address3"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Address3Field field={field} fieldState={fieldState} />
                  )}
                />
              </Box>
              <Controller
                control={control}
                name="display_address_restricted"
                render={({ field }) => <ShowUpToAddress1Field field={field} />}
              />
            </Stack>
          </Box>
          <Box maxW="326px">
            <Controller
              name="phone_number"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneNumberField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box>
            <Box>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <EmailField field={field} fieldState={fieldState} />
                )}
              />
            </Box>
          </Box>
          <Box>
            <Box maxW="326px">
              <Controller
                name="industry_id"
                control={control}
                render={({ field, fieldState }) => (
                  <IndustryField
                    field={field}
                    fieldState={fieldState}
                    industries={industries}
                  />
                )}
              />
            </Box>
            <Text fontSize="xs" color="textGray" mt={1}>
              会員登録時の業種と異なる場合は、受発注用の業種を入力してください。
            </Text>
          </Box>
          <Box>
            <FormLabel required htmlFor="number_of_employees">
              従業員数
            </FormLabel>
            <Controller
              name="number_of_employees"
              control={control}
              render={({ field, fieldState }) => (
                <EmployeesField
                  field={field}
                  isNoLabel
                  fieldState={fieldState}
                  inputProps={{ maxW: "240px" }}
                />
              )}
            />
          </Box>
          <Box>
            <FormLabel required>設立年月</FormLabel>
            <Stack gap={0.5}>
              <Flex gap={4} align="center" direction="row">
                <Flex gap={1} align="center" flex={1}>
                  <Box maxW="240px" w="full">
                    <Controller
                      name="establishment_year"
                      control={control}
                      render={({ field, fieldState }) => (
                        <EstablishmentYearField
                          field={field}
                          fieldState={fieldState}
                        />
                      )}
                    />
                  </Box>
                  <Text as="span">年</Text>
                </Flex>
                <Flex gap={1} align="center" flex={1}>
                  <Box maxW="240px" w="full">
                    <Controller
                      name="establishment_month"
                      control={control}
                      render={({ field, fieldState }) => (
                        <EstablishmentMonthField
                          field={field}
                          fieldState={fieldState}
                        />
                      )}
                    />
                  </Box>
                  <Text as="span">月</Text>
                </Flex>
              </Flex>
            </Stack>
          </Box>
          <Box>
            <Box>
              <FormLabel required htmlFor="capital_ten_thousand_yen">
                資本金
              </FormLabel>
              <Controller
                name="capital_ten_thousand_yen"
                control={control}
                render={({ field, fieldState }) => (
                  <CapitalField
                    field={field}
                    fieldState={fieldState}
                    inputProps={{ maxW: "240px" }}
                  />
                )}
              />
            </Box>
          </Box>
          <Stack gap={1}>
            <FormLabel>事業者ロゴ</FormLabel>
            <Controller
              control={control}
              name="logo_image"
              render={({
                field: { ref, onChange, value },
                fieldState: { error },
              }) => (
                <CroppableImageFileDropzone
                  onChange={(file) => {
                    if (
                      file &&
                      !validateFileSize({
                        file,
                        onError: (error) => showFlash({ error }),
                      })
                    ) {
                      return;
                    }
                    onChange(file);
                  }}
                  value={createUnifiedFile(value)}
                  label="事業者ロゴ"
                  inputRef={ref}
                  previewBoxProps={{
                    maxW: { base: "full", sm: "50%" },
                  }}
                  error={error?.message}
                  customMessage={({ sharedMessage }) => (
                    <UnorderedList>
                      <ListItem>
                        ロゴを掲載することで閲覧効果が高まります。
                      </ListItem>
                      <ListItem>
                        事業者ロゴがない場合は設計実績の建物写真などを登録してください。
                      </ListItem>
                      <ListItem>{sharedMessage}</ListItem>
                    </UnorderedList>
                  )}
                />
              )}
            />
          </Stack>
          <Box maxW="326px">
            <Controller
              name="president_name"
              control={control}
              render={({ field, fieldState }) => (
                <PresidentNameField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box>
            <Controller
              name="website_url"
              control={control}
              render={({ field, fieldState }) => (
                <WebsiteField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
        </Stack>
        <SectionTitle mt={14}>SNS</SectionTitle>
        <Stack gap={7} mt={7}>
          <Box maxW="326px">
            <Controller
              name="facebook_url"
              control={control}
              render={({ field, fieldState }) => (
                <FacebookUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="instagram_url"
              control={control}
              render={({ field, fieldState }) => (
                <InstagramUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="note_url"
              control={control}
              render={({ field, fieldState }) => (
                <NoteUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="pinterest_url"
              control={control}
              render={({ field, fieldState }) => (
                <PinterestUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="threads_url"
              control={control}
              render={({ field, fieldState }) => (
                <ThreadsUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="tiktok_url"
              control={control}
              render={({ field, fieldState }) => (
                <TiktokUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="x_url"
              control={control}
              render={({ field, fieldState }) => (
                <XUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
          <Box maxW="326px">
            <Controller
              name="youtube_url"
              control={control}
              render={({ field, fieldState }) => (
                <YoutubeUrlField field={field} fieldState={fieldState} />
              )}
            />
          </Box>
        </Stack>
        <VStack mt={{ base: 10, lg: 12 }}>
          <Divider
            border="1px var(--chakra-colors-brand-500) solid"
            opacity={1}
          />
          <Stack mt={10} gap={4}>
            <Button type="submit" isLoading={isSubmitting}>
              事業者情報 詳細登録に進む
            </Button>
          </Stack>
          <Box mt={4}>
            <SiteSeal />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default Step1;
